<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { splitName } from '@chatwoot/utils';
import countries from 'shared/constants/countries.js';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAccount } from 'dashboard/composables/useAccount';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import CompanySelector from 'dashboard/components-next/Companies/CompanySelector.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';

const props = defineProps({
  contactData: {
    type: Object,
    default: null,
  },
  isDetailsView: {
    type: Boolean,
    default: false,
  },
  isNewContact: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update']);

const { t } = useI18n();
const store = useStore();
const { currentAccount, isCloudFeatureEnabled } = useAccount();
const isDeMarcoAccount = computed(() => Number(currentAccount.value?.id || store.getters.getCurrentAccountId || 0) === 3);

const FORM_CONFIG = {
  FIRST_NAME: { field: 'firstName' },
  LAST_NAME: { field: 'lastName' },
  EMAIL_ADDRESS: { field: 'email' },
  PHONE_NUMBER: { field: 'phoneNumber' },
  CITY: { field: 'additionalAttributes.city' },
  COUNTRY: { field: 'additionalAttributes.countryCode' },
  BIO: { field: 'additionalAttributes.description' },
  COMPANY_NAME: { field: 'additionalAttributes.companyName' },
  CPF: { field: 'additionalAttributes.cpf', label: 'CPF' },
  PASTA_FISICA: { field: 'additionalAttributes.physical_folder', label: 'Número de Pasta Física' },
  GOOGLE_DRIVE: { field: 'additionalAttributes.google_drive_link', label: 'Link do Google Drive' },
  SENHA_INSS: { field: 'additionalAttributes.inss_password', label: 'Senha INSS' },
};

const SOCIAL_CONFIG = {
  FACEBOOK: 'i-ri-facebook-circle-fill',
  INSTAGRAM: 'i-ri-instagram-line',
};

const STATUS_OPTIONS = [
  'Em andamento',
  'Fase de recurso',
  'Ganho',
  'Pago',
  'Perdido',
  'Cancelado',
  'Arquivado',
];

const defaultState = {
  id: 0,
  name: '',
  email: '',
  companyId: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  customAttributes: {
    is_client: false,
    signed_contract: false,
  },
  additionalAttributes: {
    description: '',
    companyName: '',
    countryCode: '',
    country: '',
    city: '',
    cpf: '',
    physical_folder: '',
    google_drive_link: '',
    inss_password: '',
    processes: [],
    socialProfiles: {
      facebook: '',
      instagram: '',
    },
  },
};

const state = reactive({ ...defaultState });
const isProcessesEditing = ref(props.isNewContact);
const isSavingProcesses = ref(false);
const originalProcesses = ref([]);

const formatProcessDate = (dateStr) => {
  if (!dateStr) return 'Não informada';
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
};

const getStatusBadgeClass = (status) => {
  const s = (status || '').toLowerCase().trim();
  if (s.includes('andamento')) {
    return 'bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 border border-blue-200 dark:border-blue-800/60';
  }
  if (s.includes('ganho') || s.includes('pago') || s.includes('conclu')) {
    return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/60';
  }
  if (s.includes('recurso') || s.includes('viabilidade') || s.includes('pendente')) {
    return 'bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-200 dark:border-amber-800/60';
  }
  if (s.includes('perdido') || s.includes('cancelado') || s.includes('arquivado')) {
    return 'bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300 border border-rose-200 dark:border-rose-800/60';
  }
  return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border border-slate-200 dark:border-slate-700';
};

const copyProcessNumber = (number) => {
  if (!number) return;
  navigator.clipboard.writeText(number);
  useAlert('Número do processo copiado!');
};

const validationRules = {
  firstName: { required },
  email: { email },
};

const v$ = useVuelidate(validationRules, state);

const isFormInvalid = computed(() => v$.value.$invalid);
const hasCompaniesFeature = computed(
  () =>
    currentAccount.value?.id && isCloudFeatureEnabled(FEATURE_FLAGS.COMPANIES)
);
const showCompanySelector = computed(
  () =>
    hasCompaniesFeature.value &&
    (Boolean(state.companyId) || !state.additionalAttributes.companyName)
);

const emitContactUpdate = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  const isClient = state.customAttributes?.is_client === true || state.customAttributes?.is_client === 'true';
  const signedContract = state.customAttributes?.signed_contract === true || state.customAttributes?.signed_contract === 'true';

  const { firstName, lastName, ...stateWithoutNames } = state;
  const result = {
    ...stateWithoutNames,
    customAttributes: {
      is_client: isClient,
      signed_contract: signedContract,
    },
    custom_attributes: {
      is_client: isClient,
      signed_contract: signedContract,
    },
  };
  emit('update', result);
};

const prepareStateBasedOnProps = () => {
  if (props.isNewContact) {
    isProcessesEditing.value = true;
    return;
  }

  const {
    id,
    name = '',
    email: emailAddress,
    phoneNumber,
    companyId = '',
    additionalAttributes = {},
    customAttributes = {},
  } = props.contactData || {};
  const { firstName, lastName } = splitName(name || '');
  const {
    description = '',
    companyName = '',
    countryCode = '',
    country = '',
    city = '',
    cpf = '',
    physicalFolder = '', physical_folder = '', pastaFisica = '', pasta_fisica = '',
    googleDriveLink = '', google_drive_link = '', linkDrive = '', link_drive = '',
    inssPassword = '', inss_password = '', senhaInss = '', senha_inss = '',
    signedContract, signed_contract,
    isClient, is_client, cliente,
    processes = [], processos = [],
    socialTelegramUserName = '',
    socialProfiles = {},
  } = additionalAttributes || {};

  const telegramUsername = socialProfiles?.telegram || socialTelegramUserName || '';
  
  const finalPhysicalFolder = physicalFolder || physical_folder || pastaFisica || pasta_fisica || '';
  const finalGoogleDriveLink = googleDriveLink || google_drive_link || linkDrive || link_drive || '';
  const finalInssPassword = inssPassword || inss_password || senhaInss || senha_inss || '';
  
  const finalSignedContract = customAttributes?.signed_contract === true || customAttributes?.signed_contract === 'true' || signedContract === true || signedContract === 'true' || signed_contract === true || signed_contract === 'true' || false;
  const finalIsClient = customAttributes?.is_client === true || customAttributes?.is_client === 'true' || isClient === true || is_client === true || cliente === true || false;
  const finalProcesses = (processes && processes.length) ? processes : ((processos && processos.length) ? processos : []);

  originalProcesses.value = JSON.parse(JSON.stringify(finalProcesses));
  isProcessesEditing.value = false;

  Object.assign(state, {
    id,
    name,
    companyId: companyId || '',
    firstName,
    lastName,
    email: emailAddress,
    phoneNumber,
    customAttributes: {
      is_client: finalIsClient,
      signed_contract: finalSignedContract,
    },
    additionalAttributes: {
      description,
      companyName,
      countryCode,
      country,
      city,
      cpf,
      physical_folder: finalPhysicalFolder,
      google_drive_link: finalGoogleDriveLink,
      inss_password: finalInssPassword,
      processes: finalProcesses,
      socialProfiles: {
        ...socialProfiles,
        telegram: telegramUsername,
      },
    },
  });
};

const editDetailsForm = computed(() => {
  return Object.keys(FORM_CONFIG).map(key => {
    return {
      key,
      placeholder: FORM_CONFIG[key].label || t(`CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.${key}`),
    };
  });
});

const socialProfilesForm = computed(() => {
  return Object.keys(SOCIAL_CONFIG).map(key => {
    return {
      key,
      icon: SOCIAL_CONFIG[key],
      placeholder: t(`CONTACTS_LAYOUT.CARD.SOCIAL_MEDIA.${key}`),
    };
  });
});

const countryOptions = computed(() => {
  return countries.map(country => {
    return {
      id: country.id,
      name: country.name,
    };
  });
});

const isValidationField = key => {
  return ['FIRST_NAME', 'EMAIL_ADDRESS'].includes(key);
};

const getValidationKey = key => {
  return key === 'FIRST_NAME' ? 'firstName' : 'email';
};

const getFormBinding = key => {
  const field = FORM_CONFIG[key].field;

  return computed({
    get() {
      const parts = field.split('.');
      return parts.reduce((obj, part) => obj?.[part], state) ?? '';
    },
    async set(value) {
      if (key === 'FIRST_NAME') {
        state.firstName = value;
        state.name = `${state.firstName} ${state.lastName}`.trim();
      } else if (key === 'LAST_NAME') {
        state.lastName = value;
        state.name = `${state.firstName} ${state.lastName}`.trim();
      } else {
        const [base, nested] = field.split('.');
        if (nested) {
          state[base][nested] = value;
        } else {
          state[base] = value;
        }
      }

      await emitContactUpdate();
    },
  });
};

const getMessageType = key => {
  return isValidationField(key) && v$.value[getValidationKey(key)]?.$error
    ? 'error'
    : 'info';
};

const startEditingProcesses = () => {
  originalProcesses.value = JSON.parse(JSON.stringify(state.additionalAttributes.processes || []));
  isProcessesEditing.value = true;
};

const cancelEditingProcesses = () => {
  state.additionalAttributes.processes = JSON.parse(JSON.stringify(originalProcesses.value));
  isProcessesEditing.value = false;
};

const addLawyerChip = (proc, lawyerName) => {
  if (!proc.lawyers || !proc.lawyers.trim()) {
    proc.lawyers = lawyerName;
  } else if (!proc.lawyers.includes(lawyerName)) {
    proc.lawyers = `${proc.lawyers} / ${lawyerName}`;
  }
};

const addProcess = () => {
  if (!state.additionalAttributes.processes) {
    state.additionalAttributes.processes = [];
  }
  isProcessesEditing.value = true;
  state.additionalAttributes.processes.push({
    parties: '',
    court: '',
    type: '',
    number: '',
    lawyers: '',
    url: '',
    date: new Date().toISOString().split('T')[0],
    status: 'Em andamento',
    value: '',
  });
};

const removeProcess = (index) => {
  state.additionalAttributes.processes.splice(index, 1);
};

const saveProcessesDirectly = async () => {
  if (props.isNewContact) {
    return;
  }
  isSavingProcesses.value = true;
  try {
    const validProcesses = (state.additionalAttributes.processes || []).filter(
      p => p.type?.trim() || p.number?.trim() || p.parties?.trim() || p.court?.trim() || p.url?.trim() || p.value?.trim() || p.date || p.status
    );

    state.additionalAttributes.processes = validProcesses;
    originalProcesses.value = JSON.parse(JSON.stringify(validProcesses));

    await store.dispatch('contacts/update', {
      id: state.id,
      additional_attributes: {
        ...state.additionalAttributes,
        processes: validProcesses,
      },
    });

    isProcessesEditing.value = false;
    useAlert('Processos judiciais salvos com sucesso!');
    await emitContactUpdate();
  } catch (err) {
    useAlert(err.message || 'Erro ao salvar processos.');
  } finally {
    isSavingProcesses.value = false;
  }
};

const handleCountrySelection = value => {
  const selectedCountry = countries.find(option => option.id === value);
  state.additionalAttributes.country = selectedCountry?.name || '';
  emit('update', state);
};

const handleCompanySelection = async ({ id, name }) => {
  state.companyId = id || '';
  state.additionalAttributes.companyName = name || '';
  await emitContactUpdate();
};

const resetValidation = () => {
  v$.value.$reset();
};

const resetForm = () => {
  Object.assign(state, defaultState);
};

watch(
  () => props.contactData?.id,
  id => {
    if (id) prepareStateBasedOnProps();
  },
  { immediate: true }
);

defineExpose({
  state,
  resetValidation,
  isFormInvalid,
  resetForm,
});
</script>

<template>
  <div class="flex flex-col gap-6 w-full">
    <div class="flex flex-col items-start gap-2 w-full">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.TITLE') }}
      </span>
      <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
        <template v-for="item in editDetailsForm" :key="item.key">
          <ComboBox
            v-if="item.key === 'COUNTRY'"
            v-model="state.additionalAttributes.countryCode"
            :options="countryOptions"
            :placeholder="item.placeholder"
            class="[&>div>button]:h-8"
            :class="{
              '[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:!outline-transparent':
                !isDetailsView,
              '[&>div>button]:!bg-n-alpha-black2': isDetailsView,
            }"
            @update:model-value="handleCountrySelection"
          />
          <PhoneNumberInput
            v-else-if="item.key === 'PHONE_NUMBER'"
            v-model="getFormBinding(item.key).value"
            :placeholder="item.placeholder"
            :show-border="isDetailsView"
          />
          <CompanySelector
            v-else-if="item.key === 'COMPANY_NAME' && showCompanySelector"
            :model-value="state.companyId"
            :selected-name="state.additionalAttributes.companyName"
            :is-details-view="isDetailsView"
            @select="handleCompanySelection"
          />
          <Input
            v-else
            v-model="getFormBinding(item.key).value"
            :placeholder="item.placeholder"
            :message-type="getMessageType(item.key)"
            :custom-input-class="`h-8 !pt-1 !pb-1 ${
              !isDetailsView
                ? '[&:not(.error,.focus)]:!outline-transparent'
                : ''
            }`"
            class="w-full"
            @input="
              isValidationField(item.key) &&
                v$[getValidationKey(item.key)].$touch()
            "
            @blur="
              isValidationField(item.key) &&
                v$[getValidationKey(item.key)].$touch()
            "
          />
        </template>
      </div>
      
      <!-- Chavinhas Rápidas: É Cliente & Contrato Assinado -->
      <div class="flex items-center gap-6 mt-4 p-3 border border-n-slate-4 rounded-xl bg-n-alpha-black2 w-full">
        <label class="flex items-center gap-2 text-sm font-medium text-n-slate-12 cursor-pointer">
          <input 
            type="checkbox" 
            v-model="state.customAttributes.is_client" 
            @change="emitContactUpdate()" 
            class="rounded text-primary-600 focus:ring-primary-500" 
          />
          <span>⚖️ É Cliente?</span>
        </label>

        <label class="flex items-center gap-2 text-sm font-medium text-n-slate-12 cursor-pointer">
          <input 
            type="checkbox" 
            v-model="state.customAttributes.signed_contract" 
            @change="emitContactUpdate()" 
            class="rounded text-emerald-600 focus:ring-emerald-500" 
          />
          <span>✍️ Contrato Assinado</span>
        </label>
      </div>
      
      <!-- SEÇÃO COMPLETA DE PROCESSOS JURÍDICOS (MODO LEITURA BLOQUEADO E EDIÇÃO SEGURA) -->
      <div class="w-full mt-4 p-4 border border-n-slate-4 dark:border-slate-800 rounded-xl bg-n-alpha-black2">
        <!-- CABEÇALHO DA SEÇÃO DE PROCESSOS -->
        <div class="flex items-center justify-between mb-4 pb-2 border-b border-n-slate-4 dark:border-slate-800">
          <div class="flex items-center gap-2">
            <span class="text-base">⚖️</span>
            <h4 class="text-sm font-bold text-n-slate-12">Processos Judiciais do Cliente</h4>
            <span
              v-if="state.additionalAttributes.processes?.length"
              class="text-xs font-bold px-2 py-0.5 rounded-full bg-n-slate-4 dark:bg-slate-800 text-n-slate-11"
            >
              {{ state.additionalAttributes.processes.length }}
            </span>
          </div>

          <!-- BOTÕES NO CABEÇALHO -->
          <div class="flex items-center gap-2">
            <template v-if="!isProcessesEditing">
              <button
                type="button"
                @click="addProcess()"
                class="px-2.5 py-1 text-xs font-semibold text-n-brand dark:text-blue-400 hover:bg-n-brand/10 rounded-lg border border-n-brand/30 transition-colors flex items-center gap-1 cursor-pointer"
              >
                <span class="i-lucide-plus size-3.5"></span>
                <span>+ Adicionar</span>
              </button>

              <button
                type="button"
                @click="startEditingProcesses"
                class="px-3 py-1 text-xs font-semibold text-white bg-n-brand hover:brightness-110 rounded-lg shadow-xs flex items-center gap-1.5 transition-all cursor-pointer"
              >
                <span class="i-lucide-pencil size-3.5"></span>
                <span>Editar Processos</span>
              </button>
            </template>

            <span
              v-else
              class="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-blue-50 dark:bg-blue-950/50 text-n-brand dark:text-blue-400 border border-blue-200 dark:border-blue-800 flex items-center gap-1"
            >
              <span class="i-lucide-pencil size-3"></span>
              <span>Modo Edição Ativo</span>
            </span>
          </div>
        </div>

        <!-- LISTA DE PROCESSOS -->
        <div class="space-y-3.5">
          <!-- CASO 1: SEM PROCESSOS -->
          <div
            v-if="!state.additionalAttributes.processes || state.additionalAttributes.processes.length === 0"
            class="p-6 text-center border border-dashed border-n-slate-4 dark:border-slate-800 rounded-xl bg-n-solid-1 dark:bg-slate-900/40"
          >
            <span class="text-2xl block mb-1">⚖️</span>
            <p class="text-xs text-n-slate-10 mb-3">Nenhum processo judicial cadastrado para este contato.</p>
            <button
              type="button"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-n-brand hover:brightness-110 text-white text-xs font-semibold rounded-lg shadow-xs transition-all cursor-pointer"
              @click="addProcess"
            >
              <span class="i-lucide-plus size-3.5"></span>
              <span>Cadastrar Processo</span>
            </button>
          </div>

          <!-- CASO 2: ITENS DE PROCESSO -->
          <div
            v-for="(proc, index) in state.additionalAttributes.processes"
            :key="index"
            class="p-4 border rounded-xl transition-all shadow-2xs"
            :class="isProcessesEditing 
              ? 'border-n-brand/40 bg-blue-50/15 dark:bg-blue-950/20 dark:border-blue-900/40' 
              : 'border-n-slate-4 dark:border-slate-800 bg-n-solid-1 dark:bg-slate-900/40'"
          >
            <!-- CABEÇALHO DO ITEM -->
            <div class="flex items-center justify-between border-b border-n-slate-3 dark:border-slate-800 pb-2 mb-3">
              <span class="text-xs font-bold text-n-brand dark:text-blue-400 uppercase tracking-wider flex items-center gap-1.5">
                <span>⚖️</span>
                <span>Processo #{{ index + 1 }}</span>
              </span>

              <div class="flex items-center gap-2">
                <a
                  v-if="!isProcessesEditing && proc.url"
                  :href="proc.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="px-2 py-0.5 text-[11px] font-semibold text-n-brand hover:underline bg-blue-50 dark:bg-blue-950/40 rounded border border-blue-200 dark:border-blue-800/60 flex items-center gap-1 transition-colors"
                  title="Abrir processo no tribunal"
                >
                  <span class="i-lucide-external-link size-3"></span>
                  <span>Tribunal 🔗</span>
                </a>

                <button
                  v-if="!isProcessesEditing && proc.number"
                  type="button"
                  class="px-2 py-0.5 text-[11px] font-medium text-n-slate-11 hover:text-n-slate-12 bg-n-alpha-black2 rounded border border-n-slate-3 hover:border-n-slate-4 flex items-center gap-1 transition-colors cursor-pointer"
                  title="Copiar número do processo"
                  @click="copyProcessNumber(proc.number)"
                >
                  <span class="i-lucide-copy size-3"></span>
                  <span>Copiar CNJ</span>
                </button>

                <button
                  v-if="isProcessesEditing"
                  type="button"
                  @click="removeProcess(index)"
                  class="p-1 text-n-slate-10 hover:text-n-red-11 rounded transition-colors cursor-pointer"
                  title="Remover Processo"
                >
                  <span class="i-lucide-trash size-4"></span>
                </button>
              </div>
            </div>

            <!-- MODO VISUALIZAÇÃO COMPLETO (BLOQUEADO / SOMENTE LEITURA) -->
            <div v-if="!isProcessesEditing" class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
              <div>
                <span class="text-[11px] font-medium text-n-slate-10 block mb-0.5">Partes Envolvidas</span>
                <span class="text-xs font-semibold text-n-slate-12 block break-words">
                  {{ proc.parties || 'Não informado' }}
                </span>
              </div>

              <div>
                <span class="text-[11px] font-medium text-n-slate-10 block mb-0.5">Vara / Comarca</span>
                <span class="text-xs font-medium text-n-slate-12 block break-words">
                  {{ proc.court || 'Não informado' }}
                </span>
              </div>

              <div>
                <span class="text-[11px] font-medium text-n-slate-10 block mb-0.5">Tipo da Ação</span>
                <span class="text-xs font-semibold text-n-slate-12 block break-words">
                  {{ proc.type || 'Não informado' }}
                </span>
              </div>

              <div>
                <span class="text-[11px] font-medium text-n-slate-10 block mb-0.5">Número do Processo (CNJ)</span>
                <span class="font-mono text-xs font-medium text-n-slate-12 block bg-n-alpha-black2 dark:bg-slate-900 px-2 py-1 rounded border border-n-slate-3 dark:border-slate-800 select-all">
                  {{ proc.number || 'Não informado' }}
                </span>
              </div>

              <div class="sm:col-span-2">
                <span class="text-[11px] font-medium text-n-slate-10 block mb-0.5">👨‍⚖️ Advogado(s) Vinculado(s)</span>
                <span class="text-xs font-semibold text-indigo-600 dark:text-indigo-400 block break-words">
                  {{ proc.lawyers || 'Não informado' }}
                </span>
              </div>

              <div>
                <span class="text-[11px] font-medium text-n-slate-10 block mb-0.5">Data de Distribuição</span>
                <span class="text-xs font-medium text-n-slate-12 block">
                  {{ formatProcessDate(proc.date) }}
                </span>
              </div>

              <div>
                <span class="text-[11px] font-medium text-n-slate-10 block mb-0.5">Situação e Fase</span>
                <span 
                  class="inline-block px-2.5 py-0.5 rounded-md text-[11px] font-semibold tracking-wide"
                  :class="getStatusBadgeClass(proc.status)"
                >
                  {{ proc.status || 'Em andamento' }}
                </span>
              </div>

              <div>
                <span class="text-[11px] font-medium text-n-slate-10 block mb-0.5">Valor da Causa / Condenação</span>
                <span class="text-xs font-semibold text-emerald-700 dark:text-emerald-400 block">
                  {{ proc.value || 'Não informado' }}
                </span>
              </div>

              <div>
                <span class="text-[11px] font-medium text-n-slate-10 block mb-0.5">Link Direto do Processo (URL)</span>
                <a
                  v-if="proc.url"
                  :href="proc.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-xs text-n-brand dark:text-blue-400 hover:underline break-all block"
                >
                  {{ proc.url }} 🔗
                </a>
                <span v-else class="text-xs text-n-slate-10 italic block">Não informado</span>
              </div>
            </div>

            <!-- MODO EDIÇÃO (INPUTS HABILITADOS) -->
            <div v-else class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
              <div>
                <label class="block text-[11px] font-semibold text-n-slate-11 mb-1">Partes Envolvidas</label>
                <input
                  v-model="proc.parties"
                  type="text"
                  placeholder="Ex: Autor x Réu / INSS"
                  class="w-full h-8 text-xs px-3 rounded-lg border border-n-slate-4 dark:border-slate-700 bg-white dark:bg-slate-800 text-n-slate-12 outline-none focus:border-n-brand"
                />
              </div>

              <div>
                <label class="block text-[11px] font-semibold text-n-slate-11 mb-1">Vara / Comarca</label>
                <input
                  v-model="proc.court"
                  type="text"
                  placeholder="Ex: 1ª Vara Cível - Foro de..."
                  class="w-full h-8 text-xs px-3 rounded-lg border border-n-slate-4 dark:border-slate-700 bg-white dark:bg-slate-800 text-n-slate-12 outline-none focus:border-n-brand"
                />
              </div>

              <div>
                <label class="block text-[11px] font-semibold text-n-slate-11 mb-1">Tipo da Ação</label>
                <input
                  v-model="proc.type"
                  type="text"
                  placeholder="Ex: Cumprimento de Sentença"
                  class="w-full h-8 text-xs px-3 rounded-lg border border-n-slate-4 dark:border-slate-700 bg-white dark:bg-slate-800 text-n-slate-12 outline-none focus:border-n-brand"
                />
              </div>

              <div>
                <label class="block text-[11px] font-semibold text-n-slate-11 mb-1">Número do Processo (CNJ)</label>
                <input
                  v-model="proc.number"
                  type="text"
                  placeholder="Ex: 1003538-65.2026.8.26.0032"
                  class="w-full h-8 text-xs font-mono px-3 rounded-lg border border-n-slate-4 dark:border-slate-700 bg-white dark:bg-slate-800 text-n-slate-12 outline-none focus:border-n-brand"
                />
              </div>

              <div class="sm:col-span-2">
                <div class="flex items-center justify-between mb-1">
                  <label class="block text-[11px] font-semibold text-n-slate-11">👨‍⚖️ Advogado(s) Vinculado(s)</label>
                  <div v-if="isDeMarcoAccount" class="flex items-center gap-1">
                    <button
                      type="button"
                      @click="addLawyerChip(proc, 'Vinicius de Marco Fiscarelli')"
                      class="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors font-medium cursor-pointer"
                    >
                      + Dr. Vinícius
                    </button>
                    <button
                      type="button"
                      @click="addLawyerChip(proc, 'Bruno Alves Roque')"
                      class="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors font-medium cursor-pointer"
                    >
                      + Dr. Bruno
                    </button>
                    <button
                      type="button"
                      @click="addLawyerChip(proc, 'Vanderson Maciel')"
                      class="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors font-medium cursor-pointer"
                    >
                      + Dr. Vanderson
                    </button>
                  </div>
                </div>
                <input
                  v-model="proc.lawyers"
                  type="text"
                  :placeholder="isDeMarcoAccount ? 'Ex: Vinicius de Marco Fiscarelli / Bruno Alves Roque' : 'Ex: Nome do Advogado / Responsável'"
                  class="w-full h-8 text-xs px-3 rounded-lg border border-n-slate-4 dark:border-slate-700 bg-white dark:bg-slate-800 text-n-slate-12 outline-none focus:border-n-brand"
                />
              </div>

              <div>
                <label class="block text-[11px] font-semibold text-n-slate-11 mb-1">Data de Distribuição</label>
                <input
                  v-model="proc.date"
                  type="date"
                  class="w-full h-8 text-xs px-3 rounded-lg border border-n-slate-4 dark:border-slate-700 bg-white dark:bg-slate-800 text-n-slate-12 outline-none focus:border-n-brand"
                />
              </div>

              <div>
                <label class="block text-[11px] font-semibold text-n-slate-11 mb-1">Situação e Fase</label>
                <select
                  v-model="proc.status"
                  class="w-full h-8 text-xs px-3 rounded-lg border border-n-slate-4 dark:border-slate-700 bg-white dark:bg-slate-800 text-n-slate-12 outline-none focus:border-n-brand cursor-pointer"
                >
                  <option v-for="opt in STATUS_OPTIONS" :key="opt" :value="opt">
                    {{ opt }}
                  </option>
                </select>
              </div>

              <div>
                <label class="block text-[11px] font-semibold text-n-slate-11 mb-1">Valor da Causa / Condenação</label>
                <input
                  v-model="proc.value"
                  type="text"
                  placeholder="Ex: R$ 45.000,00"
                  class="w-full h-8 text-xs px-3 rounded-lg border border-n-slate-4 dark:border-slate-700 bg-white dark:bg-slate-800 text-n-slate-12 outline-none focus:border-n-brand"
                />
              </div>

              <div>
                <label class="block text-[11px] font-semibold text-n-slate-11 mb-1">Link Direto do Processo (URL)</label>
                <input
                  v-model="proc.url"
                  type="url"
                  placeholder="Ex: https://esaj.tjsp.jus.br/..."
                  class="w-full h-8 text-xs px-3 rounded-lg border border-n-slate-4 dark:border-slate-700 bg-white dark:bg-slate-800 text-n-slate-12 outline-none focus:border-n-brand"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- AÇÕES DO MODO EDIÇÃO NA SEÇÃO DE PROCESSOS -->
        <div v-if="isProcessesEditing" class="mt-4 pt-3 border-t border-n-slate-3 dark:border-slate-800 flex items-center justify-between">
          <button
            type="button"
            class="text-xs font-semibold text-n-brand dark:text-blue-400 hover:underline flex items-center gap-1 cursor-pointer"
            @click="addProcess()"
          >
            <span class="i-lucide-plus size-3.5"></span>
            <span>Adicionar Outro Processo</span>
          </button>

          <div class="flex items-center gap-2">
            <button
              v-if="!isNewContact"
              type="button"
              class="px-3 py-1.5 text-xs font-semibold text-n-slate-12 bg-n-alpha-black2 hover:bg-n-alpha-black3 rounded-lg border border-n-slate-4 dark:border-slate-700 transition-colors cursor-pointer"
              :disabled="isSavingProcesses"
              @click="cancelEditingProcesses"
            >
              Cancelar
            </button>

            <button
              type="button"
              class="px-4 py-1.5 text-xs font-semibold text-white bg-n-brand hover:brightness-110 active:scale-[0.98] rounded-lg shadow-sm flex items-center gap-1.5 transition-all cursor-pointer disabled:opacity-50"
              :disabled="isSavingProcesses"
              @click="saveProcessesDirectly"
            >
              <span v-if="!isSavingProcesses" class="i-lucide-save size-3.5"></span>
              <span v-else class="i-lucide-loader-2 size-3.5 animate-spin"></span>
              <span>{{ isSavingProcesses ? 'Salvando...' : 'Salvar Processos' }}</span>
            </button>
          </div>
        </div>
      </div>
      <!-- Fim Seção de Processos -->

    </div>
    <div class="flex flex-col items-start gap-2">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.CARD.SOCIAL_MEDIA.TITLE') }}
      </span>
      <div class="flex flex-wrap gap-2">
        <div
          v-for="item in socialProfilesForm"
          :key="item.key"
          class="flex items-center h-8 gap-2 px-2 rounded-lg"
          :class="{
            'bg-n-alpha-2 dark:bg-n-solid-2': isDetailsView,
            'bg-n-alpha-2 dark:bg-n-solid-3': !isDetailsView,
          }"
        >
          <Icon
            :icon="item.icon"
            class="flex-shrink-0 text-n-slate-11 size-4"
          />
          <input
            v-model="
              state.additionalAttributes.socialProfiles[item.key.toLowerCase()]
            "
            class="w-auto min-w-[100px] text-sm bg-transparent outline-none reset-base text-n-slate-12 dark:text-n-slate-12 placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10"
            :placeholder="item.placeholder"
            :size="item.placeholder.length"
            @input="emit('update', state)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
